import {
  Check,
  ChevronLeft,
  ChevronRight,
  Coins,
  Hexagon,
  Mountain,
  PackagePlus,
  Search,
  Settings2,
  ShieldCheck,
  TreePine,
  UserRound,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { AdminCodes } from "../AdminCodes";
import { APIClient } from "../api";
import { type AdminPlayerPage, type RegistrationPolicy, type ResourceCosts } from "../types";
import { messageOf } from "../ui/format";

export function AdminConsole({
  api,
  onClose,
  onPlayerChanged,
}: {
  api: APIClient;
  onClose: () => void;
  onPlayerChanged?: (userId: string) => Promise<void> | void;
}) {
  const [adminTab, setAdminTab] = useState<"players" | "codes">("players");
  const pageSize = 20;
  const [page, setPage] = useState<AdminPlayerPage | null>(null);
  const [registrationPolicy, setRegistrationPolicy] = useState<RegistrationPolicy | null>(null);
  const [registrationLimitDraft, setRegistrationLimitDraft] = useState("");
  const [queryDraft, setQueryDraft] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [amounts, setAmounts] = useState<ResourceCosts>({ wood: 0, ore: 0, gold: 0, crystal: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [policySuccess, setPolicySuccess] = useState("");

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      setPage(await api.adminPlayers(query, pageSize, offset));
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setLoading(false);
    }
  }, [api, offset, query]);

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  const loadRegistrationPolicy = useCallback(async () => {
    setPolicyLoading(true);
    try {
      const next = await api.adminRegistrationPolicy();
      setRegistrationPolicy(next);
      setRegistrationLimitDraft(String(next.registrationLimit));
      setPolicyError("");
    } catch (requestError) {
      setPolicyError(messageOf(requestError));
    } finally {
      setPolicyLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadRegistrationPolicy();
  }, [loadRegistrationPolicy]);

  const selected =
    page?.players.find((player) => player.userId === selectedId) ?? page?.players[0] ?? null;
  const hasGrant = Object.values(amounts).some((amount) => amount > 0);
  const fields: Array<{ key: keyof ResourceCosts; label: string; icon: typeof TreePine }> = [
    { key: "wood", label: "木", icon: TreePine },
    { key: "ore", label: "矿", icon: Mountain },
    { key: "gold", label: "金", icon: Coins },
    { key: "crystal", label: "结晶", icon: Hexagon },
  ];

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setQuery(queryDraft.trim());
    setError("");
  };

  const updateAmount = (key: keyof ResourceCosts, value: string) => {
    const amount =
      value === "" ? 0 : Math.max(0, Math.min(1_000_000, Math.floor(Number(value) || 0)));
    setAmounts((current) => ({ ...current, [key]: amount }));
  };

  const grant = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || !hasGrant || busy) return;
    const summary = fields
      .filter((field) => amounts[field.key] > 0)
      .map((field) => `${field.label} ${amounts[field.key].toLocaleString()}`)
      .join("、");
    if (!window.confirm(`确认向 ${selected.nickname} 发放 ${summary}？`)) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await api.adminGrant(selected.userId, amounts);
      setAmounts({ wood: 0, ore: 0, gold: 0, crystal: 0 });
      setSuccess(result.replayed ? "已重放上一次发放结果" : "资源已发放并记录流水");
      await loadPlayers();
      await onPlayerChanged?.(selected.userId);
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const parsedRegistrationLimit = Number(registrationLimitDraft);
  const validRegistrationLimit =
    registrationLimitDraft.trim() !== "" &&
    Number.isSafeInteger(parsedRegistrationLimit) &&
    parsedRegistrationLimit >= 0;
  const registrationLimitChanged =
    validRegistrationLimit && parsedRegistrationLimit !== registrationPolicy?.registrationLimit;
  const updateRegistrationLimit = async (event: FormEvent) => {
    event.preventDefault();
    if (!registrationLimitChanged || policyBusy) return;
    const currentCount = registrationPolicy?.registeredCount ?? 0;
    const effect =
      parsedRegistrationLimit <= currentCount
        ? "新注册会立即关闭"
        : `剩余 ${parsedRegistrationLimit - currentCount} 个名额`;
    if (
      !window.confirm(
        `确认将最大注册数改为 ${parsedRegistrationLimit.toLocaleString()}？${effect}。`,
      )
    )
      return;
    setPolicyBusy(true);
    setPolicyError("");
    setPolicySuccess("");
    try {
      const next = await api.updateAdminRegistrationLimit(parsedRegistrationLimit);
      setRegistrationPolicy(next);
      setRegistrationLimitDraft(String(next.registrationLimit));
      setPolicySuccess(next.replayed ? "已重放上一次修改结果" : "注册上限已生效");
    } catch (requestError) {
      setPolicyError(messageOf(requestError));
    } finally {
      setPolicyBusy(false);
    }
  };

  const canPrevious = offset > 0;
  const canNext = Boolean(page && offset + page.players.length < page.total);
  return (
    <div className="modal-backdrop admin-backdrop" role="presentation">
      <section
        className="admin-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-dialog-title"
      >
        <header className="admin-dialog-header">
          <div>
            <span className="eyebrow">OPERATIONS</span>
            <h2 id="admin-dialog-title">
              <ShieldCheck size={20} />
              管理控制台
            </h2>
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            aria-label="关闭管理控制台"
            title="关闭"
          >
            <X size={18} />
          </button>
        </header>
        <div className="segmented-control admin-tabs" role="group" aria-label="管理分类">
          <button
            className={adminTab === "players" ? "is-active" : ""}
            aria-pressed={adminTab === "players"}
            onClick={() => setAdminTab("players")}
          >
            玩家与资源
          </button>
          <button
            className={adminTab === "codes" ? "is-active" : ""}
            aria-pressed={adminTab === "codes"}
            onClick={() => setAdminTab("codes")}
          >
            激活码
          </button>
        </div>
        {adminTab === "codes" && <AdminCodes api={api} />}
        <div
          className="admin-dialog-body"
          style={adminTab === "codes" ? { display: "none" } : undefined}
        >
          <section className="admin-player-list" aria-labelledby="admin-player-list-title">
            <header className="admin-section-heading">
              <div>
                <strong id="admin-player-list-title">玩家列表</strong>
                <small>{page ? `${page.total} 个账号` : "读取中"}</small>
              </div>
              <form className="admin-search" onSubmit={submitSearch}>
                <input
                  value={queryDraft}
                  onChange={(event) => setQueryDraft(event.target.value)}
                  placeholder="用户名、昵称或 ID"
                  maxLength={64}
                  aria-label="搜索玩家"
                />
                <button className="icon-button" type="submit" aria-label="搜索玩家" title="搜索">
                  <Search size={16} />
                </button>
              </form>
            </header>
            <div className="admin-player-rows">
              {loading && <p className="admin-empty">正在读取玩家…</p>}
              {!loading && page?.players.length === 0 && (
                <p className="admin-empty">没有匹配的玩家</p>
              )}
              {!loading &&
                page?.players.map((player) => (
                  <button
                    key={player.userId}
                    className={`admin-player-row${selected?.userId === player.userId ? " is-selected" : ""}`}
                    onClick={() => {
                      setSelectedId(player.userId);
                      setSuccess("");
                    }}
                  >
                    <span className="admin-player-main">
                      <strong>{player.nickname}</strong>
                      <small>{player.username ? `@${player.username}` : "游客账号"}</small>
                    </span>
                    <span className="admin-player-role">
                      {player.role === "admin" ? (
                        <ShieldCheck size={14} />
                      ) : (
                        <UserRound size={14} />
                      )}
                      {player.role === "admin" ? "管理员" : "玩家"}
                    </span>
                    <span className="admin-player-resources">
                      <i>木 {player.resources.wood.toLocaleString()}</i>
                      <i>矿 {player.resources.ore.toLocaleString()}</i>
                      <i>金 {player.resources.gold.toLocaleString()}</i>
                      <i>晶 {player.resources.crystal.toLocaleString()}</i>
                    </span>
                  </button>
                ))}
            </div>
            <footer className="admin-pagination">
              <span>
                {page && page.total > 0
                  ? `${offset + 1}-${Math.min(offset + page.players.length, page.total)} / ${page.total}`
                  : "0 / 0"}
              </span>
              <div>
                <button
                  className="icon-button"
                  disabled={!canPrevious || loading}
                  onClick={() => setOffset((current) => Math.max(0, current - pageSize))}
                  aria-label="上一页"
                  title="上一页"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="icon-button"
                  disabled={!canNext || loading}
                  onClick={() => setOffset((current) => current + pageSize)}
                  aria-label="下一页"
                  title="下一页"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </footer>
          </section>
          <div className="admin-control-column">
            <section
              className="admin-registration-panel"
              aria-labelledby="admin-registration-title"
            >
              <header className="admin-section-heading">
                <div>
                  <strong id="admin-registration-title">注册容量</strong>
                  <small>
                    {policyLoading
                      ? "读取中"
                      : registrationPolicy
                        ? `已注册 ${registrationPolicy.registeredCount.toLocaleString()} 人`
                        : "读取失败"}
                  </small>
                </div>
                <Settings2 size={18} />
              </header>
              <form className="admin-registration-form" onSubmit={updateRegistrationLimit}>
                <div className="admin-registration-metrics">
                  <span>
                    <small>当前上限</small>
                    <strong>{registrationPolicy?.registrationLimit.toLocaleString() ?? "-"}</strong>
                  </span>
                  <span>
                    <small>剩余名额</small>
                    <strong>{registrationPolicy?.remaining.toLocaleString() ?? "-"}</strong>
                  </span>
                </div>
                <label className="admin-registration-input">
                  <span>最大注册数</span>
                  <input
                    type="number"
                    min="0"
                    max={Number.MAX_SAFE_INTEGER}
                    step="1"
                    inputMode="numeric"
                    value={registrationLimitDraft}
                    onChange={(event) => {
                      setRegistrationLimitDraft(event.target.value);
                      setPolicySuccess("");
                    }}
                    disabled={policyLoading || policyBusy}
                  />
                </label>
                <p className="admin-form-note">设为 0 会关闭新注册；不会删除已有玩家。</p>
                {policyError && (
                  <p className="admin-form-error" role="alert">
                    {policyError}
                  </p>
                )}
                {policySuccess && (
                  <p className="admin-form-success" role="status">
                    {policySuccess}
                  </p>
                )}
                <button
                  className="primary-command"
                  type="submit"
                  disabled={!registrationLimitChanged || policyBusy}
                >
                  <Check size={17} />
                  {policyBusy ? "保存中…" : "保存上限"}
                </button>
              </form>
            </section>
            <section className="admin-grant-panel" aria-labelledby="admin-grant-title">
              <header className="admin-section-heading">
                <div>
                  <strong id="admin-grant-title">发放资源</strong>
                  <small>{selected ? `目标：${selected.nickname}` : "先选择玩家"}</small>
                </div>
                <PackagePlus size={18} />
              </header>
              {selected ? (
                <form className="admin-grant-form" onSubmit={grant}>
                  <div className="admin-target-summary">
                    <strong>{selected.nickname}</strong>
                    <span>{selected.username ? `@${selected.username}` : "游客账号"}</span>
                    <small>
                      当前：木 {selected.resources.wood.toLocaleString()} · 矿{" "}
                      {selected.resources.ore.toLocaleString()} · 金{" "}
                      {selected.resources.gold.toLocaleString()} · 晶{" "}
                      {selected.resources.crystal.toLocaleString()}
                    </small>
                  </div>
                  <div className="admin-resource-fields">
                    {fields.map((field) => {
                      const Icon = field.icon;
                      return (
                        <label key={field.key}>
                          <span>
                            <Icon size={15} />
                            <b>{field.label}</b>
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="1000000"
                            step="1"
                            value={amounts[field.key]}
                            onChange={(event) => updateAmount(field.key, event.target.value)}
                            aria-label={`发放${field.label}`}
                          />
                        </label>
                      );
                    })}
                  </div>
                  <p className="admin-form-note">
                    单项最多 1,000,000，木/矿/金受目标仓库上限约束。
                  </p>
                  {error && (
                    <p className="admin-form-error" role="alert">
                      {error}
                    </p>
                  )}
                  {success && (
                    <p className="admin-form-success" role="status">
                      {success}
                    </p>
                  )}
                  <button className="primary-command" type="submit" disabled={busy || !hasGrant}>
                    <PackagePlus size={17} />
                    {busy ? "处理中…" : "确认发放"}
                  </button>
                </form>
              ) : (
                <p className="admin-empty">选择玩家后可发放资源</p>
              )}
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}
